/**
 * Documents on records (P23 / P24): queries + the Edit-mode staging queue.
 *
 * Staging (§3g Pattern A, Pedro 2026-10-10 "inside Edit mode"): adding, removing or
 * restoring a document is staged in the page's Edit transaction and sent on Save —
 * an upload carries its File until then. `commit()` runs the queue in staged order,
 * keeps failed ops staged for retry (usePendingOps.settle) and returns the counts
 * for the page's Save message. An RM's operations come back `pending` (filed for
 * approval, option A); Admin / Compliance ones apply at once.
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  documentsApi, type DocumentKindSpec, type DocumentOwnerType, type RecordDocuments,
} from '../api/documents';
import type { UUID } from '../api/types';
import { usePendingOps, type PendingOpsQueue } from './usePendingOps';

export const documentsKeys = {
  all: ['documents'] as const,
  kinds: ['documents', 'kinds'] as const,
  owner: (ownerType: DocumentOwnerType, ownerId: UUID | undefined) =>
    ['documents', ownerType, ownerId] as const,
};

/** The document types (static per deploy). */
export function useDocumentKinds() {
  return useQuery<DocumentKindSpec[], ApiError>({
    queryKey: documentsKeys.kinds,
    queryFn: () => documentsApi.kinds(),
    staleTime: Infinity,
  });
}

/** A record's documents, removed ones included (the panel hides them unless asked). */
export function useRecordDocuments(ownerType: DocumentOwnerType, ownerId: UUID | undefined) {
  return useQuery<RecordDocuments, ApiError>({
    queryKey: [...documentsKeys.owner(ownerType, ownerId), 'with-removed'],
    queryFn: () => documentsApi.list(ownerType, ownerId as UUID, true),
    enabled: !!ownerId,
  });
}

/** The kinds that may be attached to this record (and this kind of person). */
export function kindsFor(
  kinds: DocumentKindSpec[] | undefined,
  ownerType: DocumentOwnerType,
  personKind?: 'NATURAL' | 'LEGAL' | null,
): DocumentKindSpec[] {
  return (kinds ?? []).filter((k) => k.owner_types.includes(ownerType)
    && (!k.person_kinds || !personKind || k.person_kinds.includes(personKind)));
}

export type PendingDocumentOp =
  | { kind: 'upload'; tempKey: string; docKind: string; title: string | null; file: File }
  | { kind: 'remove'; document_id: UUID }
  | { kind: 'restore'; document_id: UUID };

// Upload ops by their temp key; remove / restore by the document (at most one each).
export const documentOpKey = (op: PendingDocumentOp): string =>
  (op.kind === 'upload' ? op.tempKey : op.document_id);

export interface DocumentCommitResult {
  applied: number;
  filed: number;
  failedKeys: Set<string>;
  firstError?: string;
}

export interface DocumentStaging {
  queue: PendingOpsQueue<PendingDocumentOp>;
  /** Stage an upload (Edit mode). */
  stageUpload: (docKind: string, title: string | null, file: File) => void;
  /** Send the staged operations; failures stay staged. */
  commit: () => Promise<DocumentCommitResult>;
}

export function useDocumentStaging(
  ownerType: DocumentOwnerType, ownerId: UUID | undefined,
): DocumentStaging {
  const queue = usePendingOps<PendingDocumentOp>(documentOpKey);
  const qc = useQueryClient();
  const { ops, settle, stage } = queue;

  const stageUpload = useCallback((docKind: string, title: string | null, file: File) => {
    stage({ kind: 'upload', tempKey: `doc-upload-${crypto.randomUUID()}`, docKind, title, file });
  }, [stage]);

  const commit = useCallback(async (): Promise<DocumentCommitResult> => {
    if (ops.length === 0 || !ownerId) return { applied: 0, filed: 0, failedKeys: new Set() };
    let applied = 0;
    let filed = 0;
    const failedKeys = new Set<string>();
    let firstError: string | undefined;
    // Staged order: a remove staged before a re-add reaches the server in that order.
    for (const op of ops) {
      try {
        const r = op.kind === 'upload'
          ? await documentsApi.upload({
            owner_type: ownerType, owner_id: ownerId, kind: op.docKind, title: op.title, file: op.file,
          })
          : op.kind === 'remove'
            ? await documentsApi.remove(op.document_id)
            : await documentsApi.restore(op.document_id);
        if (r.pending) filed++; else applied++;
      } catch (e) {
        failedKeys.add(documentOpKey(op));
        if (!firstError) firstError = e instanceof ApiError ? e.detail : 'Unexpected error.';
      }
    }
    settle(failedKeys, firstError);
    qc.invalidateQueries({ queryKey: documentsKeys.owner(ownerType, ownerId) });
    qc.invalidateQueries({ queryKey: ['change-requests'] });
    return { applied, filed, failedKeys, firstError };
  }, [ops, settle, ownerType, ownerId, qc]);

  return { queue, stageUpload, commit };
}

/** The Save message's document segment(s) (§3j: name what happened). */
export function documentResultParts(r: DocumentCommitResult): string[] {
  const s = (n: number) => (n === 1 ? '' : 's');
  const parts: string[] = [];
  if (r.applied > 0) parts.push(`${r.applied} document change${s(r.applied)} applied`);
  if (r.filed > 0) parts.push(`${r.filed} document change${s(r.filed)} filed for approval`);
  if (r.failedKeys.size > 0) {
    parts.push(`${r.failedKeys.size} document change${s(r.failedKeys.size)} failed`
      + `${r.firstError ? ` (${r.firstError})` : ''} — see Failed badges and retry`);
  }
  return parts;
}
